import { QuestionCircleOutlined } from "@ant-design/icons";
import { Col, Form, InputNumber, Popover, Row, Select, Space, Switch } from "antd";
import { AktDateRangePicker } from "components/aktDatePicker";
import { ConsentCheckboxFilter } from "components/consentCheckboxFilter";
import { TagSelect } from "components/tagSelect";
import UdfCustomFieldFilter from "features/accountsTable/components/UdfCustomFieldFilter";
import { useFetchCommunicationLimitsQuery } from "features/communicationLimitsTable/communicationLimitsApi";
import {
  useFetchCreditorSummariesQuery,
  useFetchCreditorTagsQuery,
} from "features/creditors/agencyPortal/creditorsAPI";
import { useFetchImportHistoryQuery } from "features/dataImport/agencyPortal/dataImportAPI";
import {
  collectorsWithRolesSelector,
  useFetchAgencyWorkflowStates,
  useFetchBackendConstantsQuery,
  useFetchCollectorsQuery,
} from "features/home/agencyPortal/homeAPI";
import { collectorFullName } from "features/home/utils";
import { useFetchProductOfferingsQuery } from "features/productOfferings/productOfferingsAPI";
import { useFetchDebtorTagsQuery } from "features/tags/tagsAPI";
import { useFetchAllAccountUdfCustomFieldsQuery } from "features/userDefinedFields/accountUserDefinedFieldsAPI";
import { useFetchAllDebtorUdfCustomFieldsQuery } from "features/userDefinedFields/agencyPortal/debtorUserDefinedFieldsAPI";
import { useFetchAllCreditorUdfCustomFieldsQuery } from "features/userDefinedFields/agencyPortal/creditorUserDefinedFieldsAPI";

import moment from "moment-timezone";
import { useMemo } from "react";
import styled from "styled-components";
import { useFetchDesksQuery } from "features/desksTable/desksAPI";

const StyledQuestionCircleOutlined = styled(QuestionCircleOutlined)`
  color: #1677ff;
  margin-left: 4px;
`;

const StyledHeader = styled.h4`
  font-weight: 600;
  margin-top: 0px;
  margin-bottom: 8px;
`;

const StyledSpan = styled.span`
  margin-bottom: 24px;
  display: block;
  font-size: 13px;
`;

const FilterTitle = styled.div`
  font-weight: 600;
  padding: 0 0 2px;
`;

const StyledSelect = styled(Select)`
  width: 200px;
  margin-right: 4px;
`;

export function DebtorsFiltersStep() {
  const { collectorsWithRoles: collectors } = useFetchCollectorsQuery(undefined, {
    selectFromResult: (result) => ({
      ...result,
      collectorsWithRoles: collectorsWithRolesSelector(result),
    }),
  });
  const { data: desks = [], isLoading: isDesksLoading } = useFetchDesksQuery();
  const { data: constants = {}, isLoading: isConstantsLoading } = useFetchBackendConstantsQuery();
  const { data: debtorTags = [], isLoading: isDebtorTagsLoading } = useFetchDebtorTagsQuery();
  const { data: defaultWorkflowStates = [], isLoading: isDefaultWorkflowStatesLoading } =
    useFetchAgencyWorkflowStates();
  const { data: productOfferings = [], isLoading: isProductOfferingsLoading } =
    useFetchProductOfferingsQuery();
  const { data: creditors = [], isLoading: isCreditorsLoading } = useFetchCreditorSummariesQuery();
  const { data: creditorTags = [], isLoading: isCreditorTagsLoading } = useFetchCreditorTagsQuery();
  const { data: accountImports = [], isLoading: isAccountImportsLoading } =
    useFetchImportHistoryQuery({
      type: "bulk_action_account",
      statuses: ["success", "partial_success"],
    });
  const { data: debtorImports = [], isLoading: isDebtorImportsLoading } =
    useFetchImportHistoryQuery({
      type: "bulk_action_debtor",
      statuses: ["success", "partial_success"],
    });

  const { data: accountCustomFieldsData, isLoading: isAccountCustomFieldsLoading } =
    useFetchAllAccountUdfCustomFieldsQuery();
  const { data: customFieldsData = [], isLoading: isCustomFieldsLoading } =
    useFetchAllDebtorUdfCustomFieldsQuery();
  const { data: creditorCustomFieldsData, isLoading: isCreditorCustomFieldsLoading } =
    useFetchAllCreditorUdfCustomFieldsQuery();
  const { data: communicationLimits = [], isLoading: isCommunicationLimitsLoading } =
    useFetchCommunicationLimitsQuery();
  const filterOption = (inputValue, option) => {
    const fullOptionText = option.label;
    return fullOptionText?.toLowerCase().includes(inputValue?.toLowerCase());
  };

  const creditorOptions = useMemo(
    () =>
      creditors?.map((creditor) => ({
        value: creditor.id,
        label: creditor.code ? `${creditor.name} (${creditor.code})` : creditor.name,
      })),
    [creditors],
  );

  const creditorTagOptions = useMemo(
    () =>
      creditorTags?.map((tag) => ({
        value: tag.id,
        label: tag.name,
      })),
    [creditorTags],
  );

  return (
    <>
      <StyledHeader>Filters</StyledHeader>
      <StyledSpan>Debtors matching these filter criteria will be selected.</StyledSpan>

      <Form.Item hidden name="type" />

      <FilterTitle>
        Debtor State
        <Popover
          content="This filters by the primary address of the debtor"
          title=""
          trigger="hover"
          placement="right"
        >
          <StyledQuestionCircleOutlined />
        </Popover>
      </FilterTitle>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name={["filters", "states"]} label="Include">
            <Select
              popupMatchSelectWidth={false}
              mode="multiple"
              allowClear
              filterOption={filterOption}
              options={constants?.states?.map((state) => ({
                label: `${state.display} (${state.value})`,
                value: state.value,
              }))}
              placeholder="States..."
              loading={isConstantsLoading}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={["filters", "excludeStates"]} label="Exclude">
            <Select
              popupMatchSelectWidth={false}
              mode="multiple"
              allowClear
              filterOption={filterOption}
              options={constants?.states?.map((state) => ({
                label: `${state.display} (${state.value})`,
                value: state.value,
              }))}
              placeholder="States..."
              loading={isConstantsLoading}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row>
        <Col span={12}>
          Override operating states
          <Popover
            content="If enabled, the bulk action can apply to debtors outside the operating states"
            title=""
            trigger="hover"
            placement="right"
          >
            <StyledQuestionCircleOutlined />
          </Popover>
        </Col>
        <Col span={12}>
          <Form.Item name={["filters", "ignoreOperatingStates"]}>
            <Switch checkedChildren="Enable" unCheckedChildren="Disable" />
          </Form.Item>
        </Col>
      </Row>

      <FilterTitle>Debtor Tags</FilterTitle>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name={["filters", "tagIds"]} label="Include">
            <StyledSelect
              popupMatchSelectWidth={false}
              mode="multiple"
              allowClear
              placeholder="Debtor Tags..."
              filterOption={filterOption}
              options={debtorTags.map((tag) => ({
                value: tag.id,
                label: tag.name,
              }))}
              loading={isDebtorTagsLoading}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={["filters", "excludeTagIds"]} label="Exclude">
            <StyledSelect
              popupMatchSelectWidth={false}
              mode="multiple"
              allowClear
              placeholder="Debtor Tags..."
              filterOption={filterOption}
              options={debtorTags.map((tag) => ({
                value: tag.id,
                label: tag.name,
              }))}
              loading={isDebtorTagsLoading}
            />
          </Form.Item>
        </Col>
      </Row>

      <FilterTitle>Debtor Custom Field</FilterTitle>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name={["filters", "includeDebtorCustomFieldFilters"]} label="Include">
            <UdfCustomFieldFilter
              customFieldsData={customFieldsData}
              placeholder="Add custom field filter"
              isLoading={isCustomFieldsLoading}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={["filters", "excludeDebtorCustomFieldFilters"]} label="Exclude">
            <UdfCustomFieldFilter
              customFieldsData={customFieldsData}
              placeholder="Add custom field filter"
              isLoading={isCustomFieldsLoading}
            />
          </Form.Item>
        </Col>
      </Row>

      <ConsentCheckboxFilter label="Email Consent" name={["filters", "emailConsentStatuses"]} />
      <ConsentCheckboxFilter label="Text Consent" name={["filters", "textConsentStatuses"]} />
      <ConsentCheckboxFilter
        label="AI Calls Consent"
        name={["filters", "aiCallsConsentStatuses"]}
      />

      <FilterTitle>Account Custom Field</FilterTitle>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name={["filters", "includeAccountCustomFieldFilters"]} label="Include">
            <UdfCustomFieldFilter
              customFieldsData={accountCustomFieldsData}
              placeholder="Add custom field filter"
              isLoading={isAccountCustomFieldsLoading}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={["filters", "excludeAccountCustomFieldFilters"]} label="Exclude">
            <UdfCustomFieldFilter
              customFieldsData={accountCustomFieldsData}
              placeholder="Add custom field filter"
              isLoading={isAccountCustomFieldsLoading}
            />
          </Form.Item>
        </Col>
      </Row>

      <FilterTitle>Client Custom Field</FilterTitle>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name={["filters", "includeCreditorCustomFieldFilters"]} label="Include">
            <UdfCustomFieldFilter
              customFieldsData={creditorCustomFieldsData}
              placeholder="Add custom field filter"
              isLoading={isCreditorCustomFieldsLoading}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={["filters", "excludeCreditorCustomFieldFilters"]} label="Exclude">
            <UdfCustomFieldFilter
              customFieldsData={creditorCustomFieldsData}
              placeholder="Add custom field filter"
              isLoading={isCreditorCustomFieldsLoading}
            />
          </Form.Item>
        </Col>
      </Row>

      <FilterTitle>Collectors Assigned</FilterTitle>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name={["filters", "assigneeIds"]} label="Include">
            <StyledSelect
              popupMatchSelectWidth={false}
              mode="multiple"
              allowClear
              placeholder="Collectors Assigned..."
              filterOption={filterOption}
              options={
                collectors && [
                  {
                    label: "Unassigned",
                    value: null,
                  },
                  ...(collectors.map((c) => ({
                    label: collectorFullName(c),
                    value: c.id,
                  })) ?? []),
                ]
              }
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={["filters", "excludeAssigneeIds"]} label="Exclude">
            <StyledSelect
              popupMatchSelectWidth={false}
              mode="multiple"
              allowClear
              placeholder="Collectors Assigned..."
              filterOption={filterOption}
              options={
                collectors && [
                  {
                    label: "Unassigned",
                    value: null,
                  },
                  ...(collectors.map((c) => ({
                    label: collectorFullName(c),
                    value: c.id,
                  })) ?? []),
                ]
              }
            />
          </Form.Item>
        </Col>
      </Row>

      <FilterTitle>Clients</FilterTitle>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name={["filters", "creditorIds"]} label="Include">
            <StyledSelect
              maxTagTextLength={20}
              popupMatchSelectWidth={false}
              mode="multiple"
              allowClear
              placeholder="Select Clients..."
              filterOption={filterOption}
              options={creditorOptions}
              loading={isCreditorsLoading}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={["filters", "excludeCreditorIds"]} label="Exclude">
            <StyledSelect
              maxTagTextLength={20}
              popupMatchSelectWidth={false}
              mode="multiple"
              allowClear
              placeholder="Select Clients..."
              filterOption={filterOption}
              options={creditorOptions}
              loading={isCreditorsLoading}
            />
          </Form.Item>
        </Col>
      </Row>
      <FilterTitle>Desks</FilterTitle>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name={["filters", "deskIds"]} label="Include">
            <StyledSelect
              popupMatchSelectWidth={false}
              mode="multiple"
              allowClear
              placeholder="Desks..."
              filterOption={filterOption}
              options={desks.map((desk) => ({
                label: desk.name,
                value: desk.id,
              }))}
              loading={isDesksLoading}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={["filters", "excludeDeskIds"]} label="Exclude">
            <StyledSelect
              popupMatchSelectWidth={false}
              mode="multiple"
              allowClear
              placeholder="Desks..."
              filterOption={filterOption}
              options={desks.map((desk) => ({
                label: desk.name,
                value: desk.id,
              }))}
              loading={isDesksLoading}
            />
          </Form.Item>
        </Col>
      </Row>
      <FilterTitle>Client Tags</FilterTitle>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name={["filters", "creditorTagIds"]} label="Include">
            <StyledSelect
              maxTagTextLength={20}
              popupMatchSelectWidth={false}
              mode="multiple"
              allowClear
              placeholder="Select Client Tags..."
              filterOption={filterOption}
              options={creditorTagOptions}
              maxTagCount="responsive"
              loading={isCreditorTagsLoading}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={["filters", "excludeCreditorTagIds"]} label="Exclude">
            <StyledSelect
              maxTagTextLength={20}
              popupMatchSelectWidth={false}
              mode="multiple"
              allowClear
              placeholder="Select Client Tags..."
              filterOption={filterOption}
              options={creditorTagOptions}
              maxTagCount="responsive"
              loading={isCreditorTagsLoading}
            />
          </Form.Item>
        </Col>
      </Row>
      <FilterTitle>Original Creditor</FilterTitle>
      <Col span={12}>
        <Form.Item name={["filters", "originalCreditorNames"]}>
          <TagSelect placeholder="Name (case insensitive)" />
        </Form.Item>
      </Col>

      <FilterTitle>Statuses</FilterTitle>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name={["filters", "statusCodes"]} label="Include">
            <StyledSelect
              popupMatchSelectWidth={false}
              mode="multiple"
              allowClear
              placeholder="Status..."
              filterOption={filterOption}
              options={defaultWorkflowStates.map((state) => ({
                label: [state.code, state.name].join(" - "),
                value: state.code,
                title: state.description,
              }))}
              loading={isDefaultWorkflowStatesLoading}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={["filters", "excludeStatusCodes"]} label="Exclude">
            <StyledSelect
              popupMatchSelectWidth={false}
              mode="multiple"
              allowClear
              placeholder="Status..."
              filterOption={filterOption}
              options={defaultWorkflowStates.map((state) => ({
                label: [state.code, state.name].join(" - "),
                value: state.code,
                title: state.description,
              }))}
              loading={isDefaultWorkflowStatesLoading}
            />
          </Form.Item>
        </Col>
      </Row>

      <FilterTitle>Product Offerings</FilterTitle>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name={["filters", "productOfferingIds"]} label="Include">
            <StyledSelect
              popupMatchSelectWidth={false}
              mode="multiple"
              allowClear
              placeholder="Product Offering..."
              filterOption={filterOption}
              options={productOfferings.map((productOffering) => ({
                label: productOffering.name,
                value: productOffering.id,
              }))}
              loading={isProductOfferingsLoading}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={["filters", "excludeProductOfferingIds"]} label="Exclude">
            <StyledSelect
              popupMatchSelectWidth={false}
              mode="multiple"
              allowClear
              placeholder="Product Offering..."
              filterOption={filterOption}
              options={productOfferings.map((productOffering) => ({
                label: productOffering.name,
                value: productOffering.id,
              }))}
              loading={isProductOfferingsLoading}
            />
          </Form.Item>
        </Col>
      </Row>

      <FilterTitle>Account Imports</FilterTitle>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name={["filters", "includeAccountDataImportIds"]} label="Include">
            <StyledSelect
              popupMatchSelectWidth={false}
              mode="multiple"
              allowClear
              placeholder="Select Account Imports..."
              filterOption={filterOption}
              options={accountImports.map((accountImport) => ({
                value: accountImport.id,
                label: `${accountImport.filename} - ${moment(accountImport.createdAt).format(
                  "MM/DD/YY HH:mm",
                )}`,
              }))}
              disabled={isAccountImportsLoading}
              loading={isAccountImportsLoading}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={["filters", "excludeAccountDataImportIds"]} label="Exclude">
            <StyledSelect
              popupMatchSelectWidth={false}
              mode="multiple"
              allowClear
              placeholder="Select Account Imports..."
              filterOption={filterOption}
              options={accountImports.map((accountImport) => ({
                value: accountImport.id,
                label: `${accountImport.filename} - ${moment(accountImport.createdAt).format(
                  "MM/DD/YY HH:mm",
                )}`,
              }))}
              disabled={isAccountImportsLoading}
              loading={isAccountImportsLoading}
            />
          </Form.Item>
        </Col>
      </Row>

      <FilterTitle>Debtor Imports</FilterTitle>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name={["filters", "debtorDataImportIds"]} label="Include">
            <StyledSelect
              popupMatchSelectWidth={false}
              mode="multiple"
              allowClear
              placeholder="Select Debtor Imports..."
              filterOption={filterOption}
              options={debtorImports.map((debtorImport) => ({
                value: debtorImport.id,
                label: `${debtorImport.filename} - ${moment(debtorImport.createdAt).format(
                  "MM/DD/YY HH:mm",
                )}`,
              }))}
              disabled={isDebtorImportsLoading}
              loading={isDebtorImportsLoading}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={["filters", "excludeDebtorDataImportIds"]} label="Exclude">
            <StyledSelect
              popupMatchSelectWidth={false}
              mode="multiple"
              allowClear
              placeholder="Select Debtor Imports..."
              filterOption={filterOption}
              options={debtorImports.map((debtorImport) => ({
                value: debtorImport.id,
                label: `${debtorImport.filename} - ${moment(debtorImport.createdAt).format(
                  "MM/DD/YY HH:mm",
                )}`,
              }))}
              disabled={isDebtorImportsLoading}
              loading={isDebtorImportsLoading}
            />
          </Form.Item>
        </Col>
      </Row>

      <FilterTitle>CBR Class Codes</FilterTitle>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name={["filters", "includeCbrClassCodes"]} label="Include">
            <StyledSelect
              popupMatchSelectWidth={false}
              mode="multiple"
              allowClear
              placeholder="Include CBR Class Codes..."
              filterOption={filterOption}
              // map options from constants.cbrClassCodes
              options={(constants.cbrClassCodes ?? []).map((cbrClassCode) => ({
                label: cbrClassCode.display,
                value: cbrClassCode.value,
              }))}
              loading={isConstantsLoading}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={["filters", "excludeCbrClassCodes"]} label="Exclude">
            <StyledSelect
              popupMatchSelectWidth={false}
              mode="multiple"
              allowClear
              placeholder="Exclude CBR Class Codes..."
              filterOption={filterOption}
              options={(constants.cbrClassCodes ?? []).map((cbrClassCode) => ({
                label: cbrClassCode.display,
                value: cbrClassCode.value,
              }))}
              loading={isConstantsLoading}
            />
          </Form.Item>
        </Col>
      </Row>

      <FilterTitle>Turnover Date</FilterTitle>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name={["filters", "turnoverDate"]} label="Include">
            <AktDateRangePicker picker="date" allowClear />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={["filters", "excludeTurnoverDate"]} label="Exclude">
            <AktDateRangePicker picker="date" allowClear />
          </Form.Item>
        </Col>
      </Row>

      <FilterTitle>Open Date</FilterTitle>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name={["filters", "openDate"]} label="Include">
            <AktDateRangePicker picker="date" allowClear />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={["filters", "excludeOpenDate"]} label="Exclude">
            <AktDateRangePicker picker="date" allowClear />
          </Form.Item>
        </Col>
      </Row>
      <FilterTitle>Follow-up Date</FilterTitle>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name={["filters", "followUpDate"]} label="Include">
            <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={["filters", "excludeFollowUpDate"]} label="Exclude">
            <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
          </Form.Item>
        </Col>
      </Row>
      <FilterTitle>Upload Date</FilterTitle>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name={["filters", "uploadDate"]} label="Include">
            <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={["filters", "excludeUploadDate"]} label="Exclude">
            <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
          </Form.Item>
        </Col>
      </Row>
      <FilterTitle>Last Call Date</FilterTitle>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name={["filters", "lastCallDate"]} label="Include">
            <AktDateRangePicker picker="date" allowClear />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={["filters", "excludeLastCallDate"]} label="Exclude">
            <AktDateRangePicker picker="date" allowClear />
          </Form.Item>
        </Col>
      </Row>

      <FilterTitle>Last Payment Date</FilterTitle>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name={["filters", "lastPaymentDate"]} label="Include">
            <AktDateRangePicker picker="date" allowClear />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={["filters", "excludeLastPaymentDate"]} label="Exclude">
            <AktDateRangePicker picker="date" allowClear />
          </Form.Item>
        </Col>
      </Row>

      <FilterTitle>Out Of Statute Date</FilterTitle>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name={["filters", "includeStatuteOfLimitationsExpiryDate"]} label="Include">
            <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={["filters", "excludeStatuteOfLimitationsExpiryDate"]} label="Exclude">
            <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
          </Form.Item>
        </Col>
      </Row>

      <FilterTitle>Last Letter Sent Date</FilterTitle>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name={["filters", "includeLastLetterDate"]} label="Include">
            <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={["filters", "excludeLastLetterDate"]} label="Exclude">
            <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
          </Form.Item>
        </Col>
      </Row>

      <FilterTitle>Last Email Date</FilterTitle>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name={["filters", "includeLastEmailDate"]} label="Include">
            <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={["filters", "excludeLastEmailDate"]} label="Exclude">
            <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
          </Form.Item>
        </Col>
      </Row>

      <FilterTitle>
        Payment Scheduled
        <Popover
          content="This filters for accounts that have a scheduled payment"
          title=""
          trigger="hover"
          placement="right"
        >
          <StyledQuestionCircleOutlined />
        </Popover>
      </FilterTitle>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name={["filters", "includeScheduledPaymentDate"]} label="Include">
            <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={["filters", "excludeScheduledPaymentDate"]} label="Exclude">
            <AktDateRangePicker allowEmpty={[true, true]} picker="date" allowClear />
          </Form.Item>
        </Col>
      </Row>

      <FilterTitle>Payment Method Types</FilterTitle>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item name={["filters", "includePaymentMethodTypes"]} label="Include">
            <StyledSelect
              popupMatchSelectWidth={false}
              maxTagTextLength={20}
              mode="multiple"
              placeholder="Select Payment Method Types..."
              filterOption={filterOption}
              options={constants.paymentMethodTypes?.map(({ value, display }) => ({
                value,
                label: display,
              }))}
              maxTagCount="responsive"
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name={["filters", "excludePaymentMethodTypes"]} label="Exclude">
            <StyledSelect
              popupMatchSelectWidth={false}
              maxTagTextLength={20}
              mode="multiple"
              placeholder="Select Payment Method Types..."
              filterOption={filterOption}
              options={constants.paymentMethodTypes?.map(({ value, display }) => ({
                value,
                label: display,
              }))}
              maxTagCount="responsive"
            />
          </Form.Item>
        </Col>
      </Row>

      <FilterTitle>Communication Compliance Limits</FilterTitle>
      <Row gutter={12}>
        <Col span={12}>
          <Form.Item
            name={["filters", "compliesWithCommunicationLimitRuleIds"]}
            label="Complies with"
          >
            <StyledSelect
              popupMatchSelectWidth={false}
              mode="multiple"
              allowClear
              placeholder="Communication Compliance Limits..."
              filterOption={filterOption}
              options={communicationLimits.map((communicationLimitRule) => ({
                label: communicationLimitRule.name,
                value: communicationLimitRule.id,
              }))}
              loading={isCommunicationLimitsLoading}
            />
          </Form.Item>
        </Col>
      </Row>

      <FilterTitle>Total Balance:</FilterTitle>
      <Space>
        <Form.Item name={["filters", "minAccountBalance"]}>
          <InputNumber placeholder="Min" controls={false} min="0" prefix="$" step="0.01" />
        </Form.Item>
        <Form.Item name={["filters", "maxAccountBalance"]}>
          <InputNumber placeholder="Max" controls={false} min="0" prefix="$" step="0.01" />
        </Form.Item>
      </Space>

      <FilterTitle>Score:</FilterTitle>
      <Space>
        <Form.Item name={["filters", "minExternalScore"]}>
          <InputNumber placeholder="Min" controls={false} min="0" step="0.01" />
        </Form.Item>
        <Form.Item name={["filters", "maxExternalScore"]}>
          <InputNumber placeholder="Max" controls={false} min="0" step="0.01" />
        </Form.Item>
      </Space>

      <FilterTitle>Intelitech I-Score:</FilterTitle>
      <Space>
        <Form.Item name={["filters", "minIntelitechIScore"]}>
          <InputNumber placeholder="Min" controls={false} min="0" step="1" max="999" />
        </Form.Item>
        <Form.Item name={["filters", "maxIntelitechIScore"]}>
          <InputNumber placeholder="Max" controls={false} min="0" max="999" step="1" />
        </Form.Item>
      </Space>

      <FilterTitle>Intelitech Medal-Score:</FilterTitle>
      <Form.Item name={["filters", "intelitechMedalScore"]}>
        <StyledSelect
          allowClear
          mode="multiple"
          placeholder="Select one..."
          popupMatchSelectWidth={false}
          options={constants?.intelitechMedalScores ?? []}
        />
      </Form.Item>

      <FilterTitle>Intelitech Q-Score:</FilterTitle>
      <Space>
        <Form.Item name={["filters", "minIntelitechQScore"]}>
          <InputNumber placeholder="Min" controls={false} min="0" step="0.01" />
        </Form.Item>
        <Form.Item name={["filters", "maxIntelitechQScore"]}>
          <InputNumber placeholder="Max" controls={false} min="0" step="0.01" />
        </Form.Item>
      </Space>

      <FilterTitle>Intelitech R-Score:</FilterTitle>
      <Space>
        <Form.Item name={["filters", "minIntelitechRScore"]}>
          <InputNumber placeholder="Min" controls={false} min="0" step="0.01" />
        </Form.Item>
        <Form.Item name={["filters", "maxIntelitechRScore"]}>
          <InputNumber placeholder="Max" controls={false} min="0" step="0.01" />
        </Form.Item>
      </Space>

      <FilterTitle>Intelitech FPG-Value:</FilterTitle>
      <Space>
        <Form.Item name={["filters", "intelitechFpgValue"]}>
          <InputNumber placeholder="Min" controls={false} min="0" step="0.01" />
        </Form.Item>
        <Form.Item name={["filters", "intelitechFpgValue"]}>
          <InputNumber placeholder="Max" controls={false} min="0" step="0.01" />
        </Form.Item>
      </Space>
    </>
  );
}
